import {
  CopyOutlined,
  DeleteOutlined,
  FileImageOutlined,
  LinkOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import {
  Button,
  Form,
  Image,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Tag,
  Typography,
} from "antd";
import { useEffect, useMemo, useState } from "react";
import CustomAttributesEditor from "../CustomAttributesEditor";
import OutputFormatPicker from "../OutputFormatPicker";
import { DEFAULT_COLOR_PROFILE, sizeRules } from "../specDefaults";
import PrinterSelect from "../PrinterSelect";

const { Text, Title } = Typography;

const emptySpec = () => ({ unit: "px", dpi: 300, customAttributes: {} });
const emptyProvider = () => ({
  colorProfile: DEFAULT_COLOR_PROFILE,
  specs: [emptySpec()],
});

export default function ProductFormModal({
  open,
  product,
  categories = [],
  onCancel,
  onSave,
  canCreate = true,
  canDelete = true,
}) {
  const [form] = Form.useForm();
  // Unsaved providers/specs can always be dropped; saved ones need delete permission
  const canRemove = (path) => canDelete || !form.getFieldValue([...path, "id"]);
  const [thumbnail, setThumbnail] = useState("");
  // Inactive Amazon categories stay listed only for the product already using one
  const categoryOptions = useMemo(
    () =>
      categories
        .filter(
          (category) =>
            category.isActive || category.id === product?.categoryId,
        )
        .map((category) => {
          const codes = (category.code || []).join(", ");
          return {
            value: category.id,
            label: codes ? `${category.name} · ${codes}` : category.name,
            search: `${category.name} ${codes}`,
          };
        }),
    [categories, product?.categoryId],
  );

  useEffect(() => {
    if (!open) return;
    const initial = product || {
      status: "active",
      providers: [{ colorProfile: DEFAULT_COLOR_PROFILE, specs: [] }],
    };
    form.setFieldsValue(initial);
    setThumbnail(initial.thumbnail || "");
  }, [open, product, form]);

  const submit = async () => {
    try {
      const values = await form.validateFields();
      await onSave({
        ...values,
        productCode: values.productCode.trim().toUpperCase(),
      });
    } catch (error) {
      if (!error.errorFields) throw error;
    }
  };
  const addProvider = () =>
    form.setFieldValue("providers", [
      ...(form.getFieldValue("providers") || []),
      emptyProvider(),
    ]);
  // Copy without ids so the backend creates a new provider and new specs
  const duplicateProvider = (index, add) => {
    const { id, ...source } = form.getFieldValue(["providers", index]) || {};
    add(
      {
        ...source,
        // The copy is usually for another printer, so pick it again
        printerId: undefined,
        specs: (source.specs || []).map(({ id: specId, ...spec }) => ({
          ...spec,
          customAttributes: { ...(spec.customAttributes || {}) },
        })),
      },
      index + 1,
    );
  };

  return (
    <Modal
      className="specstudio-dialog-modal specstudio-product-modal"
      open={open}
      onCancel={onCancel}
      onOk={submit}
      okText={product ? "Lưu thay đổi" : "Tạo sản phẩm"}
      cancelText="Hủy"
      width={1080}
      destroyOnClose
      title={null}
    >
      <div className="specstudio-product-form-heading">
        <div>
          <Text className="specstudio-eyebrow">
            SPECSTUDIO · PRODUCT EDITOR
          </Text>
          <Title level={2}>
            {product ? "Chỉnh sửa sản phẩm" : "Thêm sản phẩm"}
          </Title>
          <Text type="secondary">
            Thông tin chung ở cấp sản phẩm. Quy cách riêng được thiết lập theo
            từng nhà in và vùng in.
          </Text>
        </div>
        {product?.productCode && (
          <Tag color="purple" className="specstudio-sku">
            {product.productCode}
          </Tag>
        )}
      </div>

      <Form form={form} layout="vertical" requiredMark>
        <div className="specstudio-product-form-layout">
          <section className="specstudio-form-section">
            <div className="specstudio-form-section-heading">
              <span className="specstudio-section-index">01</span>
              <div>
                <Title level={4}>Sản phẩm gốc</Title>
                <Text type="secondary">
                  Base SKU này dùng chung cho tất cả nhà in.
                </Text>
              </div>
            </div>
            <div className="specstudio-product-fields">
              <div className="specstudio-form-two-col">
                <Form.Item
                  name="name"
                  label="Tên sản phẩm"
                  rules={[
                    {
                      required: true,
                      whitespace: true,
                      message: "Nhập tên sản phẩm",
                    },
                  ]}
                >
                  <Input size="large" placeholder="VD: T-Shirt 2D" />
                </Form.Item>
                <Form.Item
                  name="productCode"
                  label="Base SKU Code"
                  rules={[
                    {
                      required: true,
                      whitespace: true,
                      message: "Nhập Base SKU",
                    },
                    { max: 50, message: "Tối đa 50 ký tự" },
                    {
                      pattern: /^[A-Za-z0-9_-]+$/,
                      message: "Dùng chữ, số, dấu gạch ngang hoặc gạch dưới",
                    },
                  ]}
                  extra="Viết hoa, không dấu, không khoảng trắng."
                >
                  <Input
                    size="large"
                    placeholder="VD: TS2D hoặc TS-2D"
                    style={{
                      textTransform: "uppercase",
                      fontFamily: "monospace",
                    }}
                  />
                </Form.Item>
              </div>
              <Form.Item
                name="categoryId"
                label="Danh mục"
                extra="Danh mục sản phẩm Amazon, quản lý ở module Danh mục."
              >
                <Select
                  size="large"
                  allowClear
                  showSearch
                  optionFilterProp="search"
                  placeholder="Chọn danh mục Amazon"
                  options={categoryOptions}
                />
              </Form.Item>
              <Form.Item name="generalNotes" label="Ghi chú chung">
                <Input.TextArea
                  className="specstudio-notes-input"
                  rows={4}
                  placeholder="Lưu ý dùng chung cho sản phẩm, ví dụ quy trình hoặc yêu cầu file…"
                />
              </Form.Item>
              <Form.Item name="status" label="Trạng thái">
                <Select
                  size="large"
                  options={[
                    { value: "active", label: "Đang hoạt động" },
                    { value: "archived", label: "Đã lưu trữ" },
                  ]}
                />
              </Form.Item>
            </div>
          </section>

          <aside className="specstudio-image-panel">
            <div className="specstudio-form-section-heading compact">
              <span className="specstudio-section-index">ẢNH</span>
              <div>
                <Title level={5}>Ảnh đại diện</Title>
                <Text type="secondary">Nhận diện nhanh trong thư viện.</Text>
              </div>
            </div>
            <div className="specstudio-image-preview">
              {thumbnail ? (
                <Image
                  src={thumbnail}
                  alt="Product preview"
                  preview={false}
                  onError={() => setThumbnail("")}
                />
              ) : (
                <div className="specstudio-image-empty">
                  <FileImageOutlined />
                  <Text>Ảnh phối sản phẩm</Text>
                  <Text type="secondary">Xem trước ảnh tại đây</Text>
                </div>
              )}
            </div>
            <div className="specstudio-thumbnail-field">
              <Text>Đường dẫn ảnh</Text>
              <Input
                size="large"
                prefix={<LinkOutlined />}
                placeholder="Dán URL ảnh HTTPS"
                value={thumbnail}
                onChange={(event) => {
                  setThumbnail(event.target.value);
                  form.setFieldValue("thumbnail", event.target.value);
                }}
              />
            </div>
            <Text className="specstudio-image-hint">
              Ảnh cần truy cập công khai để hiển thị cho nhóm.
            </Text>
          </aside>
        </div>

        <section className="specstudio-provider-editor">
          <div className="specstudio-provider-editor-heading">
            <div className="specstudio-form-section-heading">
              <span className="specstudio-section-index">02</span>
              <div>
                <Title level={4}>Nhà in &amp; vùng in</Title>
                <Text type="secondary">
                  Mỗi nhà in có hồ sơ màu và quy cách vùng in riêng.
                </Text>
              </div>
            </div>
            {canCreate && (
              <Button
                className="specstudio-add-provider-header"
                icon={<PlusOutlined />}
                onClick={addProvider}
              >
                Thêm nhà in
              </Button>
            )}
          </div>
          <Form.List name="providers">
            {(providers, { add, remove }) => (
              <div className="specstudio-provider-editor-list">
                {providers.map((provider, index) => (
                  <div
                    className="specstudio-provider-editor-card"
                    key={provider.key}
                  >
                    <Form.Item name={[provider.name, "id"]} hidden>
                      <Input />
                    </Form.Item>
                    <div className="specstudio-provider-card-heading">
                      <div className="specstudio-provider-card-title">
                        <span className="specstudio-section-index">
                          {String(index + 1).padStart(2, "0")}
                        </span>
                        <div>
                          <Text className="specstudio-eyebrow">
                            FULFILLMENT PROVIDER
                          </Text>
                          <Title level={5}>Nhà in {index + 1}</Title>
                        </div>
                      </div>
                      <Space>
                        {canCreate && (
                          <Button
                            type="text"
                            size="small"
                            icon={<CopyOutlined />}
                            onClick={() => duplicateProvider(provider.name, add)}
                          >
                            Duplicate nhà in
                          </Button>
                        )}
                        {canRemove(["providers", provider.name]) && (
                          <Button
                            danger
                            type="text"
                            size="small"
                            icon={<DeleteOutlined />}
                            onClick={() => remove(provider.name)}
                          >
                            Bỏ nhà in
                          </Button>
                        )}
                      </Space>
                    </div>
                    <div className="specstudio-form-two-col">
                      <Form.Item
                        name={[provider.name, "printerId"]}
                        label="Tên nhà in"
                        rules={[{ required: true, message: "Chọn nhà in" }]}
                      >
                        <PrinterSelect />
                      </Form.Item>
                      <Form.Item
                        name={[provider.name, "colorProfile"]}
                        label="Hệ màu"
                      >
                        <Input
                          size="large"
                          placeholder="VD: sRGB IEC61966-2.1"
                        />
                      </Form.Item>
                    </div>
                    <Form.Item
                      name={[provider.name, "notes"]}
                      label="Lưu ý riêng của nhà in"
                    >
                      <Input.TextArea
                        className="specstudio-notes-input"
                        rows={3}
                        placeholder="Yêu cầu riêng của nhà in này, ví dụ cách đặt tên file, quy trình gửi file…"
                      />
                    </Form.Item>
                    <Form.List name={[provider.name, "specs"]}>
                      {(specs, { add: addSpec, remove: removeSpec }) => (
                        <div className="specstudio-area-list">
                          {specs.map((spec, areaIndex) => (
                            <div
                              className="specstudio-area-editor"
                              key={spec.key}
                            >
                              <Form.Item name={[spec.name, "id"]} hidden>
                                <Input />
                              </Form.Item>
                              <div className="specstudio-area-editor-heading">
                                <div>
                                  <Text className="specstudio-eyebrow">
                                    VÙNG {areaIndex + 1}
                                  </Text>
                                  <Text strong>Vùng in</Text>
                                </div>
                                {canRemove([
                                  "providers",
                                  provider.name,
                                  "specs",
                                  spec.name,
                                ]) && (
                                  <Button
                                    danger
                                    type="text"
                                    size="small"
                                    icon={<DeleteOutlined />}
                                    aria-label="Xóa vùng in"
                                    onClick={() => removeSpec(spec.name)}
                                  />
                                )}
                              </div>
                              <div className="specstudio-area-grid">
                                <Form.Item
                                  name={[spec.name, "areaName"]}
                                  label="Vị trí in"
                                  rules={[
                                    {
                                      required: true,
                                      whitespace: true,
                                      message: "Nhập tên vùng in",
                                    },
                                  ]}
                                >
                                  <Input placeholder="VD: Full Front, Left Chest" />
                                </Form.Item>
                                <Form.Item
                                  name={[spec.name, "width"]}
                                  label="Chiều rộng"
                                  rules={sizeRules}
                                >
                                  <InputNumber min={0} placeholder="4500" />
                                </Form.Item>
                                <Form.Item
                                  name={[spec.name, "height"]}
                                  label="Chiều cao"
                                  rules={sizeRules}
                                >
                                  <InputNumber min={0} placeholder="5400" />
                                </Form.Item>
                                <Form.Item
                                  name={[spec.name, "unit"]}
                                  label="Đơn vị"
                                >
                                  <Select
                                    options={["px", "cm", "inch"].map(
                                      (unit) => ({ value: unit, label: unit }),
                                    )}
                                  />
                                </Form.Item>
                                <Form.Item
                                  name={[spec.name, "dpi"]}
                                  label="DPI"
                                >
                                  <InputNumber min={1} placeholder="300" />
                                </Form.Item>
                              </div>
                              <div className="specstudio-area-grid secondary">
                                <Form.Item
                                  name={[spec.name, "outputFormat"]}
                                  label="Định dạng output"
                                >
                                  <OutputFormatPicker />
                                </Form.Item>
                                <Form.Item
                                  name={[spec.name, "backgroundType"]}
                                  label="Yêu cầu nền"
                                >
                                  <Select
                                    allowClear
                                    placeholder="Chọn nền"
                                    options={[
                                      "Transparent",
                                      "Solid Color",
                                      "White",
                                      "Custom",
                                    ].map((value) => ({ value, label: value }))}
                                  />
                                </Form.Item>
                              </div>
                              <div className="specstudio-template-row">
                                <Form.Item
                                  name={[spec.name, "templateFileUrl"]}
                                  label="Template resource"
                                >
                                  <Input
                                    prefix={<LinkOutlined />}
                                    placeholder="Dán direct URL / Google Drive / S3 CDN"
                                  />
                                </Form.Item>
                                <Form.Item
                                  name={[spec.name, "sortOrder"]}
                                  hidden
                                >
                                  <InputNumber />
                                </Form.Item>
                              </div>
                              <Form.Item
                                name={[spec.name, "customAttributes"]}
                                label="Thông số tùy chỉnh"
                              >
                                <CustomAttributesEditor />
                              </Form.Item>
                            </div>
                          ))}
                          {canCreate && (
                            <Button
                              className="specstudio-add-area"
                              type="dashed"
                              block
                              icon={<PlusOutlined />}
                              onClick={() => addSpec(emptySpec())}
                            >
                              Thêm vùng in
                            </Button>
                          )}
                        </div>
                      )}
                    </Form.List>
                  </div>
                ))}
                {providers.length === 0 && (
                  <div className="specstudio-provider-empty">
                    <Text>Chưa có nhà in</Text>
                    {canCreate && (
                      <Button type="link" onClick={() => add(emptyProvider())}>
                        Thêm nhà in đầu tiên
                      </Button>
                    )}
                  </div>
                )}
              </div>
            )}
          </Form.List>
        </section>
      </Form>
      <div className="specstudio-product-form-footer">
        <Text type="secondary">
          Nhà in và quy cách sẽ được lưu cùng sản phẩm.
        </Text>
      </div>
    </Modal>
  );
}
